import { KeyboardTypeOptions } from 'react-native';

/* eslint-disable global-require */
export type CommonScreenProps = {
  id: string;
  questionCategory: string;
};

const GOALS_CATEGORY = 'Goals';
const PROFILE_CATEGORY = 'About You';
const HABITS_CATEGORY = 'Habits';
const INFO_CATEGORY = 'Info';

export type ChoiceScreenProps = {
  type: 'SingleChoice' | 'MultiChoice' | 'InfoInput';
  title: string[];
  prompts: string[];
  options: string[];
};

export type ValidationScreenProps = {
  type: 'ValidationScreen';
  textField: string;
  blurb: string;
  imageURL: string;
};

export type BreakScreenProps = {
  type: 'BreakScreen';
  title: string;
  header?: string;
  blurb: string[];
  blurbFunc?: (input: string[], responses: any) => string[];
  imageURL?: string;
};

export type InfoEntryProps = {
  type: 'MultiEntry' | 'SingleEntry' | 'DesiredWeightInputScreen';
  title: string[];
  label?: string;
  keyboardType?: KeyboardTypeOptions | undefined;
  validationFunc?: (input: string) => string;
};

export type ProgressScreenProps = {
  type: 'ProgressBar';
  title: string;
  blurb: string[];
  duration: number;
};

export type ScreenProps = CommonScreenProps &
  (
    | ChoiceScreenProps
    | InfoEntryProps
    | BreakScreenProps
    | ValidationScreenProps
    | ProgressScreenProps
  );

export const ScreenFlow: ScreenProps[] = [
  /*
  {
    id: 'ValidDevice',
    type: 'ValidationScreen',
    questionCategory: PROFILE_CATEGORY,
    colorCode: PROFILE_COLOR,
    blurb: "If you're on an Android or non-pro iPhone we'd still love to stay in touch!",
    imageURL: require('../../assets/images/iPhone Illustration.png'),
    textField: 'SnapCalorie is currently only supported on iPhone 12-14 Pros.\n\nDo you have a supported iPhone Pro?',
  },
  */
  {
    id: 'FitnessGoal',
    type: 'SingleChoice',
    questionCategory: GOALS_CATEGORY,
    title: ['FITNESS_GOALS'],
    prompts: ['What is your primary weight loss goal?'],
    options: [
      'Lose 1-25 lbs for good',
      'Lose 26-50 lbs for good',
      'Lose over 50 lbs for good',
      'Maintain weight and get fit',
      'Undecided',
    ],
  },
  {
    id: 'Sex',
    type: 'SingleChoice',
    questionCategory: PROFILE_CATEGORY,
    title: ['SEX_ASSIGNMENT'],
    prompts: [
      'Hormones and sex play a role in how our bodies break down nutrients.\n\n' + 'Which sex best describes you?',
    ],
    options: ['Male', 'Female', 'Other'],
  },
  {
    id: 'Gender',
    type: 'SingleChoice',
    questionCategory: PROFILE_CATEGORY,
    title: ['GENDER_ASSIGNMENT'],
    prompts: ['What gender do you identify with?'],
    options: ['Male', 'Female', 'Non-Binary', 'Other', 'Prefer to not say'],
  },
  {
    id: 'AgeRange',
    type: 'SingleChoice',
    questionCategory: PROFILE_CATEGORY,
    title: ['AGE_ASSIGNMENT'],
    prompts: ['What is your age range?'],
    options: ['20s', '30s', '40s', '50s', '60+'],
  },
  {
    id: 'CurrentHeightWeight',
    type: 'MultiEntry',
    questionCategory: PROFILE_CATEGORY,
    title: ['Please enter height and weight:'],
  },
  {
    id: 'IdealWeight',
    type: 'DesiredWeightInputScreen',
    questionCategory: PROFILE_CATEGORY,
    title: ['Enter your desired weight:'],
  },
  {
    id: 'UsingAScale',
    type: 'BreakScreen',
    questionCategory: INFO_CATEGORY,
    title: 'INFORMATION_ON_PROCESS',
    header: 'Can I use a scale?',
    blurb: [
      'If you want to log food yourself, you can always add and edit meal entries manually.\n' + 
      'Try saving your favorite meal for quick and accurate logging!',
    ],
    imageURL: require('../../assets/images/food/food_manual_log.png'),
  },
  {
    id: 'Motivations',
    type: 'MultiChoice',
    questionCategory: PROFILE_CATEGORY,
    title: ['MOTIVATION_FACTORS'],
    prompts: [
      'There can be many motivating factors keeping you to a plan.\n\nDo you have any important events coming up?',
    ],
    options: ['Holidays', 'Wedding', 'Summer', 'Competition', 'Birthday', 'Other', 'None'],
  },
  /*
  // Model prediction slides
  {
    id: 'AccuracyInfo',
    type: 'BreakScreen',
    questionCategory: GOALS_CATEGORY,
    colorCode: GOALS_COLOR,
    title: 'INFORMATION_ON_ACCURACY',
    header: 'How accurate is it?',
    blurb: [
      'Academic publications show our approach, on average, has around half the error of a professional nutritionist.',
    ],
    imageURL: require('../../assets/images/accuracy.png'),
  },
  {
    id: 'HowItWorks',
    type: 'BreakScreen',
    questionCategory: GOALS_CATEGORY,
    colorCode: GOALS_COLOR,
    title: 'INFORMATION_ON_ACCURACY',
    header: 'How does SnapCalorie know what’s on my plate?',
    blurb: [
      'SnapCalorie uses artificial intelligence and human experts to visually label the types of food in your meal.',
    ],
    imageURL: require('../../assets/images/food/food_6.png'),
  },
  {
    id: 'UseAScale',
    type: 'BreakScreen',
    questionCategory: GOALS_CATEGORY,
    colorCode: GOALS_COLOR,
    title: 'INFORMATION_ON_ACCURACY',
    header: 'Can I still use a scale?',
    blurb: ['Yes! Let SnapCalorie suggest the type of food then type in the gram measurements in portion.'],
  },
  */
  {
    id: 'HealthConditions',
    type: 'MultiChoice',
    questionCategory: HABITS_CATEGORY,
    title: ['HEALTH_CONDITIONS'],
    prompts: ['Are you at risk for any of the following health complications?'],
    options: [
      'Athsma',
      'Anxiety',
      'Depression',
      'NASH',
      'IBS',
      'Heart Disease or Stroke',
      'Insomnia',
      'High Blood Pressure',
      'Diabetes',
      'Kidney Disease',
      'None',
    ],
  },
  {
    id: 'ExcerciseAmount',
    type: 'SingleChoice',
    questionCategory: HABITS_CATEGORY,
    title: ['EXCERCISE_CHECK'],
    prompts: [
      'Just 20 minutes of daily activity is shown to improve metabolism\n\n' +
        'How often do you work out on average each week?',
    ],
    options: [
      'Little or no excercise',
      '1-3 times per week',
      '4-5 times per week',
      '6-7 times per week',
      'Daily, high intensity',
    ],
  },
  {
    id: 'ProgressBar',
    type: 'ProgressBar',
    questionCategory: HABITS_CATEGORY,
    title: 'PROGRESS_BAR',
    blurb: ['Crunching your numbers...'],
    duration: 5,
  },
  {
    id: 'InitialEmail',
    type: 'SingleEntry',
    questionCategory: HABITS_CATEGORY,
    title: ['Enter your email to get your customized plan:'],
    validationFunc: (input: string) => {
      if (input.indexOf('@') === -1 || input.indexOf('.') === -1) {
        return 'Please enter a valid email.';
      }
      return '';
    },
    keyboardType: 'email-address',
    label: 'Email',
  },
  {
    id: 'Eligible',
    type: 'BreakScreen',
    questionCategory: HABITS_CATEGORY,
    title: 'CONGRATULATIONS_INFO',
    header: 'Congratulations!',
    blurb: [
      'Your survey results indicate you are a great fit for the SnapCalorie program. ',
      'We anticipate you will reach your goal weight of __GOAL__ lbs in __DAYS__ days.',
    ],
    blurbFunc: (blurb, surveyResponses) => {
      let daysToGoalLow = 365;
      let goalWeight = 180;
      if (surveyResponses.CurrentHeightWeight && surveyResponses.IdealWeight) {
        // eslint-disable-next-line prefer-destructuring
        goalWeight = surveyResponses.IdealWeight[0];
        const lbsToGoal = Math.abs(goalWeight - surveyResponses.CurrentHeightWeight[2]);
        daysToGoalLow = Math.ceil(lbsToGoal * 3.5);
      }

      return blurb.map((el) => el.replace('__GOAL__', String(goalWeight)).replace('__DAYS__', String(daysToGoalLow)));
    },
  },
];

export default ScreenFlow;